/**
 * Uma aba, desenhada FORA do `nz-tabset`.
 *
 * Normalmente a aba é desenhada pelo `TabsContainerRenderer`, que precisa do
 * conteúdo dentro do `<nz-tab>` para o NG-Zorro montar sob demanda. Este
 * renderer existe para o caso em que o `NodeHost` recebe um nó `tab` direto —
 * no editor, ao pré-visualizar uma aba isolada, e em documento migrado que
 * ainda tenha uma `tab` solta.
 *
 * Sem ele, um `tab` fora do lugar sumiria da tela sem aviso, que é exatamente o
 * tipo de perda silenciosa que a migração encontrou em produção.
 */
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';

import { ScreenNode } from '../../model/node-props';
import { trackByNodeId } from '../../model/screen-index';
import { NodeHostComponent } from '../node-host.component';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { childLayoutStyles } from './child-layout';

@Component({
  selector: 'tem-tab-container',
  standalone: true,
  imports: [CommonModule, NodeHostComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="tem-tab">
      @for (child of children(); track trackBy(0, child)) {
        <div [ngStyle]="layoutOf(child)">
          <tem-node-host [node]="child" [form]="form()" [mode]="mode()" />
        </div>
      }
    </div>
  `,
  styleUrl: './tab.renderer.scss'
})
export class TabContainerRenderer implements NodeRenderer<'tab'> {

  readonly node = input.required<ScreenNode<'tab'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  readonly trackBy = trackByNodeId;

  readonly children = computed<readonly ScreenNode[]>(() => this.node().children ?? []);

  layoutOf(child: ScreenNode): Readonly<Record<string, string>> {
    return childLayoutStyles(child);
  }
}
